import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Grid,
    Link,
    Paper,
    Skeleton,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowBack } from '@mui/icons-material';
import CodeChip from '../coding/CodeChip';
import CodePicker from '../coding/CodePicker';
import CodebookPanel from '../coding/CodebookPanel';
import ConfirmDialog from '../coding/ConfirmDialog';
import { utCodesFor, rcCodes } from '../coding/codes';
import { SPEAKER_COLORS, SPEAKER_LABELS } from '../coding/codingColors';
import { NotFound } from '../layout/SystemState';
import { Progress } from '../adjudication/parts';
import useCodingKeys from '../adjudication/useCodingKeys';
import { getAnnotationCodebook, getSheet, saveCode, submitLesson } from '../../services/annotationService';

const RETRY_MS = 3000;
const timeOf = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// One utterance of the sheet: number, speaker, text and its two cells; the selected row opens its
// pickers. Speakers and text only, and the caller's own drafts.
const SheetRow = React.forwardRef(({ line, selected, codebook, onSelect, onPick }, ref) => (
    <Box
        ref={ref}
        role="row"
        aria-selected={selected}
        onClick={() => onSelect(line.seq)}
        sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '36px 1fr 52px 52px', sm: '44px 76px 1fr 64px 64px' },
            columnGap: 1.5,
            alignItems: 'center',
            px: 1.5,
            py: 1,
            cursor: 'pointer',
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow: selected ? (t) => `inset 3px 0 0 ${t.palette.primary.main}` : 'none',
            bgcolor: selected ? (t) => alpha(t.palette.primary.main, 0.07) : 'transparent',
            transition: 'background-color 150ms',
            '&:hover': selected ? {} : { bgcolor: 'action.hover' },
        }}
    >
        <Typography role="cell" variant="caption" color="text.secondary">{line.seq}</Typography>
        <Typography role="cell" variant="body2" sx={{ fontWeight: 600, color: SPEAKER_COLORS[line.speakerRole], display: { xs: 'none', sm: 'block' } }}>
            {SPEAKER_LABELS[line.speakerRole]}
        </Typography>
        <Typography role="cell" variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{line.text}</Typography>
        <Box role="cell"><CodeChip code={line.ut} coded aria-label={line.ut ? `UT ${line.ut}` : 'UT not coded'} /></Box>
        <Box role="cell"><CodeChip code={line.rc} coded aria-label={line.rc ? `RC ${line.rc}` : 'RC not coded'} /></Box>
        {selected && codebook && (
            <Box onClick={(e) => e.stopPropagation()} sx={{ gridColumn: '1 / -1', display: 'grid', gap: 1, pt: 1.25, pb: 0.5, pl: { xs: 0, sm: '56px' } }}>
                <CodePicker id={`ut-${line.seq}`} label="UT" codes={utCodesFor(codebook, line.speakerRole)} value={line.ut || null} onChange={(code) => onPick(line.seq, { ut: code })} />
                <CodePicker id={`rc-${line.seq}`} label="RC" codes={rcCodes(codebook)} value={line.rc || null} onChange={(code) => onPick(line.seq, { rc: code })} />
            </Box>
        )}
    </Box>
));

// /annotate/:assignmentId (wf-annotate-lesson 2–3, YES-227): the whole lesson, coded utterance by
// utterance. Every pick is saved as it is made (no Save button); codes are drafts until Submit, then
// final (decision 2026-10-22). No unsure mark, no other annotator's codes, no model output.
const LessonSheet = () => {
    const { assignmentId } = useParams();
    const navigate = useNavigate();
    const [sheet, setSheet] = useState(null);
    const [codebook, setCodebook] = useState(null);
    const [status, setStatus] = useState('loading');
    const [selected, setSelected] = useState(null);
    const [save, setSave] = useState({ state: 'idle', at: null });
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const pending = useRef(new Map());
    const rowRefs = useRef(new Map());

    const load = useCallback(async () => {
        try {
            const [s, c] = await Promise.all([getSheet(assignmentId), getAnnotationCodebook(assignmentId)]);
            setSheet(s);
            setCodebook(c);
            setSave({ state: 'idle', at: s.savedAt || null });
            // Continue opens the sheet at the first uncoded utterance
            setSelected((s.lines.find((l) => !l.ut || !l.rc) || s.lines[0])?.seq ?? null);
            setStatus('ready');
        } catch (err) {
            const code = err.response?.status;
            setStatus(code === 404 ? 'notFound' : code === 409 ? 'submitted' : 'error');
        }
    }, [assignmentId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (selected != null) rowRefs.current.get(selected)?.scrollIntoView?.({ block: 'nearest' });
    }, [selected]);

    const lines = useMemo(() => sheet?.lines || [], [sheet]);
    const coded = lines.filter((l) => l.ut && l.rc).length;
    const total = lines.length;
    const current = lines.find((l) => l.seq === selected);

    // Saves every waiting pick, one loop at a time; picks made meanwhile go in the next pass. A failure
    // keeps the pick waiting and tries again.
    const flushing = useRef(null);
    const flush = useCallback(() => {
        // A running loop picks up new picks itself; callers (Submit) can await it
        if (flushing.current) return flushing.current;
        if (!pending.current.size) return Promise.resolve();
        flushing.current = run();
        return flushing.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentId]);

    const run = async () => {
        setSave((s) => ({ ...s, state: 'saving' }));
        try {
            while (pending.current.size) {
                const [seq, codes] = pending.current.entries().next().value;
                try {
                    const result = await saveCode(assignmentId, seq, codes);
                    if (pending.current.get(seq) === codes) pending.current.delete(seq);
                    setSave({ state: pending.current.size ? 'saving' : 'saved', at: result.savedAt });
                } catch (err) {
                    if (err.response?.status === 409) { setStatus('submitted'); return; }
                    if (err.response?.status === 400) { pending.current.delete(seq); continue; }
                    setSave((s) => ({ ...s, state: 'retrying' }));
                    return;
                }
            }
        } finally {
            flushing.current = null;
        }
    };

    useEffect(() => {
        if (save.state !== 'retrying') return undefined;
        const t = setTimeout(flush, RETRY_MS);
        return () => clearTimeout(t);
    }, [save.state, flush]);

    const pick = (seq, codes) => {
        setSheet((s) => ({ ...s, lines: s.lines.map((l) => (l.seq === seq ? { ...l, ...codes } : l)) }));
        pending.current.set(seq, { ...(pending.current.get(seq) || {}), ...codes });
        flush();
    };

    const move = (step) => {
        const i = lines.findIndex((l) => l.seq === selected);
        const next = lines[Math.min(lines.length - 1, Math.max(0, i + step))];
        if (next) setSelected(next.seq);
    };

    useCodingKeys({
        enabled: status === 'ready' && !confirming && Boolean(current && codebook),
        utCodes: current && codebook ? utCodesFor(codebook, current.speakerRole).map((e) => e.code) : [],
        rcCodes: codebook ? rcCodes(codebook).map((e) => e.code) : [],
        onUt: (code) => current && pick(current.seq, { ut: code }),
        onRc: (code) => current && pick(current.seq, { rc: code }),
    });

    useEffect(() => {
        if (status !== 'ready' || confirming) return undefined;
        const onKey = (e) => {
            const tag = e.target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
            if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const submit = async () => {
        setBusy(true);
        setSubmitError(null);
        try {
            await flush();
            await submitLesson(assignmentId);
            navigate('/adjudicate', { replace: true });
        } catch (err) {
            setBusy(false);
            setSubmitError(err.response?.status === 409
                ? `${err.response?.data?.error || 'Some utterances are left to code'}. Nothing was submitted.`
                : 'Couldn’t submit. Your drafts are saved; try again.');
        }
    };

    if (status === 'notFound') return <NotFound />;
    if (status === 'submitted') {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
                <Paper sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1">This lesson is submitted</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Your codes are final. A submitted lesson is not reopened.</Typography>
                    <Button variant="contained" component={RouterLink} to="/adjudicate" sx={{ mt: 3 }}>Go to My queue</Button>
                </Paper>
            </Box>
        );
    }
    if (status === 'error') {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load the lesson.</Alert>
            </Box>
        );
    }

    const saveLine = {
        saving: 'Saving…',
        retrying: 'Not saved, retrying',
        saved: save.at ? `draft saved ${timeOf(save.at)}` : 'draft saved',
        idle: save.at ? `draft saved ${timeOf(save.at)}` : 'no codes yet',
    }[save.state];
    const left = total - coded;

    return (
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
            <Link component={RouterLink} to="/adjudicate" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', fontWeight: 600 }}>
                <ArrowBack sx={{ fontSize: 16 }} /> My queue
            </Link>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'flex-end' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mt: 0.5 }}>
                <Box>
                    <Typography variant="h2" component="h1">{sheet ? sheet.label : <Skeleton width={200} />}</Typography>
                    <Typography variant="body2" color={save.state === 'retrying' ? 'warning.dark' : 'text.secondary'} sx={{ mt: 0.5 }} aria-live="polite">
                        {sheet ? `${total} utterances · ${saveLine}` : <Skeleton width={180} />}
                    </Typography>
                </Box>
                {sheet && <Progress done={coded} total={total} width={260} noun="coded" />}
                <Box sx={{ textAlign: { md: 'right' } }}>
                    <Button variant="contained" size="large" disabled={!sheet || left > 0} onClick={() => { setSubmitError(null); setConfirming(true); }}>Submit lesson</Button>
                    {sheet && left > 0 && <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.5 }}>{left} left to code</Typography>}
                </Box>
            </Box>
            <Typography variant="caption" component="p" sx={{ mt: 2, mb: 1.5, color: 'text.disabled', display: { xs: 'none', md: 'block' } }}>
                Keys: letter for UT (Q, P, E, F+y/s/q, R+y/s/q, O) · 1–5 for RC · ↓ next utterance
            </Typography>

            <Grid container spacing={2.5} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                    <Paper role="table" aria-label="Coding sheet" sx={{ overflow: 'hidden' }}>
                        <Box role="row" sx={{ display: 'grid', gridTemplateColumns: { xs: '36px 1fr 52px 52px', sm: '44px 76px 1fr 64px 64px' }, columnGap: 1.5, px: 1.5, py: 1, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
                            {['#', 'Speaker', 'Utterance', 'UT', 'RC'].map((h) => (
                                <Typography key={h} role="columnheader" variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: h === 'Speaker' ? { xs: 'none', sm: 'block' } : 'block' }}>{h}</Typography>
                            ))}
                        </Box>
                        <Box sx={{ maxHeight: { md: 'calc(100vh - 280px)' }, overflowY: 'auto' }}>
                            {!sheet && [0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={44} sx={{ mx: 1.5 }} />)}
                            {lines.map((line) => (
                                <SheetRow
                                    key={line.seq}
                                    ref={(el) => (el ? rowRefs.current.set(line.seq, el) : rowRefs.current.delete(line.seq))}
                                    line={line}
                                    selected={line.seq === selected}
                                    codebook={codebook}
                                    onSelect={setSelected}
                                    onPick={pick}
                                />
                            ))}
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4} sx={{ position: { md: 'sticky' }, top: { md: 72 } }}>
                    {codebook && current
                        ? <CodebookPanel codebook={codebook} speaker={current.speakerRole} highlight={{ ut: current.ut ? [current.ut] : [], rc: current.rc ? [current.rc] : [] }} />
                        : <Skeleton variant="rounded" height={420} />}
                </Grid>
            </Grid>

            <ConfirmDialog
                open={confirming}
                title={`Submit ${sheet?.label}?`}
                confirmLabel="Submit lesson"
                busy={busy}
                error={submitError}
                onConfirm={submit}
                onClose={() => setConfirming(false)}
            >
                <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1 }}>All {total} utterances are coded.</Typography>
                <Typography variant="body2">After you submit, your codes are final and can’t be changed. If something needs a second look, it goes to adjudication.</Typography>
            </ConfirmDialog>
        </Box>
    );
};

export default LessonSheet;
